import { useEffect, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { WingmanIcon } from '@components/Navigation/icons';
import { useWingmanEmbed } from '@views/WingmanBoard/useWingmanEmbed';
import { WingmanEmbedOverlay } from '@views/WingmanBoard/WingmanEmbedOverlay';

const MIN_WIDTH = 400;
const MAX_WIDTH = 800;

/* The global Wingman slide-out — the floating right-edge button on every authed page. It
   replaced the FLOX Copilot and hosts the side dashboard's panel view (?embedded=1&panel=1)
   through the same /wingman/* proxy and postMessage bridge as the full-page WingmanBoard.
   The iframe mounts on first open and stays mounted afterwards, so reopening is instant and
   users who never open the panel never load Wingman at all. */

export function WingmanPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [panelWidth, setPanelWidth] = useState(MIN_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const { iframeRef, embedStatus, embedUrl, embedAttempt, retryConnection } =
    useWingmanEmbed({ panel: true, active: hasOpened });

  const openPanel = () => {
    setIsOpen(true);
    setHasOpened(true);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <>
      <button
        type='button'
        onClick={() => (isOpen ? setIsOpen(false) : openPanel())}
        className={`fixed right-0 top-1/2 z-topBar bg-primary text-white p-3 rounded-l-lg shadow-lg hover:bg-primary/90 transition-all ${
          isOpen ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
        }`}
        title='Open Wingman'
      >
        <WingmanIcon className='w-9 h-9' />
      </button>

      <div
        className={`fixed top-16 bottom-0 right-0 bg-white shadow-2xl z-pageHeader transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${isResizing ? 'transition-none' : ''}`}
        style={{ width: `${panelWidth}px` }}
      >
        <div
          className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 transition-colors ${
            isResizing ? 'bg-primary/40' : ''
          }`}
          onMouseDown={handleMouseDown}
        >
          <div className='absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-primary/30 rounded-r' />
        </div>

        <div className='flex items-center justify-between px-4 py-3 border-b bg-gray-50'>
          <div className='flex items-center gap-2'>
            <WingmanIcon className='w-5 h-5 text-primary' />
            <span className='font-semibold text-gray-900'>Wingman</span>
          </div>
          <button
            type='button'
            onClick={() => setIsOpen(false)}
            className='p-1.5 text-gray-400 hover:text-gray-600 rounded'
            title='Minimize'
          >
            <ChevronRightIcon className='w-5 h-5' />
          </button>
        </div>

        <div className='relative flex-1'>
          {hasOpened && (
            <iframe
              key={embedAttempt}
              ref={iframeRef}
              src={embedUrl}
              title='Wingman'
              className='h-full w-full border-0'
            />
          )}
          {hasOpened && (
            <WingmanEmbedOverlay
              embedStatus={embedStatus}
              onRetry={retryConnection}
            />
          )}
        </div>
      </div>

      {/* While dragging the resize handle, a transparent shield keeps mouse events on THIS
          document — without it the same-origin iframe swallows mousemove/mouseup the moment the
          cursor crosses it, freezing the resize (and leaving isResizing latched if the button is
          released over the iframe). */}
      {isResizing && <div className='fixed inset-0 z-pageHeader cursor-col-resize' />}

      {isOpen && (
        <div
          className='fixed inset-0 bg-black bg-opacity-25 z-30 lg:hidden'
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
